/* frontend/src/components/MasonryGrid.jsx
   ------------------------------------------------------------------
   Shared masonry layout for Dashboard.jsx's .dash-skill-grid and
   Skills.jsx's .skills-grid. CSS Grid can't do this: a grid row's track
   height is always the tallest occupant sharing that row index — true
   regardless of whether placement is automatic or explicit — so a short
   card (AI Prompting) sharing row 1 with a tall one (Sales & Negotiation)
   always leaves a gap before row 2 starts, no matter how row 2's cards
   are placed. CSS multi-column doesn't work either: it pours content
   sequentially by a computed height budget, which doesn't guarantee
   "one card per column in row 1, left to right" — it can silently stack
   two short cards in column 1 before column 2 ever starts.

   This measures each card's REAL rendered height (ResizeObserver, so it
   also reacts to async content like Precision Storytelling's Quill art
   loading after mount) and does a shortest-column-first placement pass,
   positioning cards via absolute top/left. Deliberately does NOT nest
   cards into per-column wrapper divs — they stay flat siblings in their
   original DOM order (index i === the order the caller passed them in),
   so tab/reading order is always just document order, regardless of how
   masonry visually arranges them. That's the whole reason this isn't
   just a `column-count` CSS rule or a manual grouped-columns component.

   Column math replicates the old CSS grid's exact breakpoint:
   `repeat(auto-fit, minmax(max(280px, calc((100% - 40px)/3)), 1fr))`
   — max 3 columns, 280px floor per column, 20px gap.

   Usage — caller owns each item's actual wrapper (key, className,
   animation, etc.); MasonryGrid only supplies the computed position
   style and a ref callback for measurement:

     <MasonryGrid itemCount={items.length}>
       {(i, style, ref) => (
         <div key={items[i].id} ref={ref} style={style}>...</div>
       )}
     </MasonryGrid>
*/
import { useState, useEffect, useRef, useCallback, useLayoutEffect, useMemo } from 'react';

const GAP = 20;
const MIN_COL_WIDTH = 280;
const MAX_COLS = 3;

const computeColumns = (containerWidth) => {
  if (!containerWidth) return { count: MAX_COLS, width: 0 };
  for (let n = MAX_COLS; n > 1; n--) {
    const width = (containerWidth - GAP * (n - 1)) / n;
    if (width >= MIN_COL_WIDTH) return { count: n, width };
  }
  return { count: 1, width: containerWidth };
};

const MasonryGrid = ({ itemCount, children }) => {
  const containerRef = useRef(null);
  const itemElsRef = useRef([]);
  const itemObserverRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [itemHeights, setItemHeights] = useState([]);

  // Container width — the only input to column count/width. The
  // useLayoutEffect below only guarantees .observe() is *called* before
  // paint, not that the observer's first callback has *fired* yet — that
  // delivery is inherently async, so containerWidth stays 0 for at least
  // the first render or two. The isReady/hasRevealed gate further down is
  // what actually hides that gap from view.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w != null) setContainerWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // One shared ResizeObserver instance for every card (cheaper than one
  // per card, and entries arrive batched). Cards are matched back to
  // their index via the tracked node array, not via a closure, since the
  // observer instance itself is created once and reused.
  useLayoutEffect(() => {
    const ro = new ResizeObserver((entries) => {
      setItemHeights((prev) => {
        let next = prev;
        for (const entry of entries) {
          const idx = itemElsRef.current.indexOf(entry.target);
          if (idx === -1) continue;
          const h = entry.contentRect.height;
          if (next[idx] !== h) {
            if (next === prev) next = prev.slice();
            next[idx] = h;
          }
        }
        return next;
      });
    });
    itemObserverRef.current = ro;
    return () => ro.disconnect();
  }, []);

  // Keep the heights array sized to itemCount (grid growing/shrinking —
  // e.g. an admin-gated card appearing) without losing already-measured
  // values for items that keep the same index.
  useLayoutEffect(() => {
    setItemHeights((prev) => {
      if (prev.length === itemCount) return prev;
      const next = new Array(itemCount).fill(0);
      for (let i = 0; i < Math.min(prev.length, itemCount); i++) next[i] = prev[i];
      return next;
    });
    itemElsRef.current.length = itemCount;
  }, [itemCount]);

  const setItemRef = useCallback((index) => (node) => {
    const prevNode = itemElsRef.current[index];
    if (prevNode && itemObserverRef.current) itemObserverRef.current.unobserve(prevNode);
    itemElsRef.current[index] = node || null;
    if (node && itemObserverRef.current) itemObserverRef.current.observe(node);
  }, []);

  // Shortest-column-first placement pass, walked in item order (0..N-1 —
  // the caller's DOM order). Ties go to the lowest column index, so with
  // every column starting at 0, the first `count` items land one per
  // column left to right — reproducing the old grid's row-1 order
  // exactly — before any column-height comparison actually matters.
  const { positions, containerHeight, columnWidth } = useMemo(() => {
    const { count, width } = computeColumns(containerWidth);
    const colHeights = new Array(count).fill(0);
    const pos = [];
    for (let i = 0; i < itemCount; i++) {
      let col = 0;
      for (let c = 1; c < count; c++) {
        if (colHeights[c] < colHeights[col]) col = c;
      }
      pos.push({ top: colHeights[col], left: col * (width + GAP) });
      colHeights[col] += (itemHeights[i] || 0) + GAP;
    }
    const maxHeight = colHeights.length ? Math.max(...colHeights.map((h) => Math.max(h - GAP, 0))) : 0;
    return { positions: pos, containerHeight: maxHeight, columnWidth: width };
  }, [containerWidth, itemHeights, itemCount]);

  // True once we have a real container width AND a real height for every
  // currently-rendered item — i.e. the layout pass just computed is
  // actually trustworthy, not placeholder zeros. Before this, `columnWidth`
  // and `itemHeights` entries are 0/unmeasured (ResizeObserver's first
  // delivery is inherently asynchronous — ".observe()" inside
  // useLayoutEffect only guarantees the call happens before paint, not that
  // its callback has fired yet), so positions computed above are
  // meaningless. That used to render as a `columnWidth || '100%'`
  // full-width flash that snapped narrower card by card as measurements
  // trickled in.
  const isReady = containerWidth > 0 && itemHeights.length === itemCount && itemHeights.every((h) => h > 0);

  // Sticky — once we've revealed a real layout at least once, stay
  // revealed (and keep transitions enabled) for any later legitimate
  // reflow (resize, a height genuinely changing). Only the very first
  // settle is hidden.
  const [hasRevealed, setHasRevealed] = useState(false);
  useEffect(() => { if (isReady) setHasRevealed(true); }, [isReady]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        height: containerHeight,
        opacity: isReady ? 1 : 0,
        transition: 'opacity 0.2s ease, height 0.2s ease',
      }}
    >
      {Array.from({ length: itemCount }, (_, i) => {
        const p = positions[i] || { top: 0, left: 0 };
        const style = {
          position: 'absolute',
          top: p.top,
          left: p.left,
          width: columnWidth,
          // No transition until the first real reveal — otherwise the
          // instant, invisible (opacity:0) snapping during the initial
          // settle would still be "in flight" the moment opacity flips to
          // 1, showing as a residual slide into place instead of the
          // final layout just appearing.
          transition: hasRevealed ? 'top 0.2s ease, left 0.2s ease, width 0.2s ease' : 'none',
        };
        return children(i, style, setItemRef(i));
      })}
    </div>
  );
};

export default MasonryGrid;
